import { useEffect, useState } from "react";
import { supabase } from "./supabase.js";
import { useAuth } from "./auth.js";
import { formatDateOnly } from "./datetime.js";
import { Button, Card } from "./ui.js";

/** What the portal needs to know about the licence behind the active business (rpc_portal_license_status, migration 0049).
 *  ok: nothing to say. expiring: ends within 7 days. grace: ended 1-3 days ago, everything still works. lapsed: locked. */
export interface LicenseStatus {
  state: "ok" | "expiring" | "grace" | "lapsed";
  /** Last day of the licence (YYYY-MM-DD); null when open-ended or when nothing ever started. */
  endsOn: string | null;
  /** Last day of the grace period. */
  graceEndsOn: string | null;
  /** expiring: days until the end date. grace: days until grace runs out (0 = today is the last day). */
  daysLeft: number | null;
  /** lapsed only: expired, none, suspended, revoked, trial, not_started. */
  reason: string | null;
  /** The platform's support email (Admin > Developer info); empty when not set yet. */
  supportEmail: string;
}

const REFRESH_MS = 5 * 60_000;

/** Reads the licence status for a business, again whenever the tab regains focus and every few minutes (a renewal or a
 *  lapse shows without a reload). `loaded` turns true after the first answer; a failed read counts as answered with no
 *  status, so a network blip never locks anyone out (the phone gate and the database stay authoritative). */
export function useLicenseStatus(businessId: string | null): { status: LicenseStatus | null; loaded: boolean } {
  const [state, setState] = useState<{ businessId: string | null; status: LicenseStatus | null; loaded: boolean }>({ businessId: null, status: null, loaded: false });

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    async function load() {
      const { data, error } = await supabase.rpc("rpc_portal_license_status", { p_business_id: businessId });
      if (cancelled) return;
      const row = Array.isArray(data) ? data[0] : data;
      const status: LicenseStatus | null =
        !error && row
          ? { state: row.license_state, endsOn: row.ends_on ?? null, graceEndsOn: row.grace_ends_on ?? null, daysLeft: row.days_left ?? null, reason: row.reason ?? null, supportEmail: row.support_email ?? "" }
          : null;
      setState({ businessId, status, loaded: true });
    }
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    const onWake = () => document.visibilityState !== "hidden" && void load();
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [businessId]);

  // An answer for another business (the user switched) is not an answer for this one.
  const current = state.businessId === businessId;
  return { status: current ? state.status : null, loaded: current && state.loaded };
}

function inDays(n: number): string {
  return n === 0 ? "today" : n === 1 ? "tomorrow" : `in ${n} days`;
}

/** The sentence for the banner, or null when there is nothing to say. */
export function licenseNotice(s: LicenseStatus | null): { tone: "warning" | "danger"; text: string } | null {
  if (!s || !s.endsOn || s.daysLeft === null) return null;
  if (s.state === "expiring") {
    return {
      tone: "warning",
      text: `Your licence ends on ${formatDateOnly(s.endsOn)} (${inDays(s.daysLeft)}). Contact your provider to renew so your receptionist keeps answering calls.`,
    };
  }
  if (s.state === "grace" && s.graceEndsOn) {
    const left = s.daysLeft === 0 ? "the end of today" : `${formatDateOnly(s.graceEndsOn)} (${s.daysLeft === 1 ? "1 day" : `${s.daysLeft} days`} left)`;
    return {
      tone: "danger",
      text: `Licence expired. Your licence ended on ${formatDateOnly(s.endsOn)}. Everything keeps working until ${left}, then calls stop being answered and this portal is locked. Contact your provider to renew now.`,
    };
  }
  return null;
}

export function LicenseBanner({ status }: { status: LicenseStatus | null }) {
  const notice = licenseNotice(status);
  if (!notice) return null;
  const danger = notice.tone === "danger";
  return (
    <div
      role={danger ? "alert" : "status"}
      style={{
        marginBottom: 16,
        padding: "12px 16px",
        borderRadius: 10,
        fontSize: 14,
        lineHeight: 1.45,
        background: danger ? "var(--rc-danger-soft)" : "var(--rc-warning-soft)",
        color: danger ? "var(--rc-danger)" : "var(--rc-warning)",
        border: "1px solid currentColor",
      }}
    >
      {notice.text}
      {status?.supportEmail && (
        <>
          {" "}
          Support: <a href={`mailto:${status.supportEmail}`} style={{ color: "inherit", fontWeight: 600 }}>{status.supportEmail}</a>
        </>
      )}
    </div>
  );
}

/** Shown instead of the whole portal once the licence has lapsed. */
export function LicenseLapsed({ status, businessName }: { status: LicenseStatus; businessName: string | null }) {
  const { signOut } = useAuth();
  const why =
    status.reason === "expired" && status.endsOn
      ? `The licence for ${businessName ?? "this business"} ended on ${formatDateOnly(status.endsOn)} and the grace period has finished.`
      : `The licence for ${businessName ?? "this business"} is not active.`;
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, background: "var(--rc-bg)" }}>
      <Card style={{ maxWidth: 480, width: "100%" }}>
        <h1 className="rc-h1" style={{ marginBottom: 12 }}>Access is paused</h1>
        <p style={{ margin: "0 0 12px", lineHeight: 1.5 }}>{why}</p>
        <p style={{ margin: "0 0 20px", lineHeight: 1.5, color: "var(--rc-text-muted)" }}>
          Calls to your receptionist are not being answered until it is renewed, and your bookings, customers and settings are kept safe. Please contact your provider to get access back.
        </p>
        {status.supportEmail && (
          <p style={{ margin: "0 0 20px", lineHeight: 1.5 }}>
            Support: <a href={`mailto:${status.supportEmail}`} style={{ fontWeight: 600 }}>{status.supportEmail}</a>
          </p>
        )}
        <Button variant="secondary" onClick={() => void signOut()}>
          Sign out
        </Button>
      </Card>
    </div>
  );
}
